import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    computed,
    ElementRef,
    inject,
    OnDestroy,
    signal,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LineViewService } from './line-view.service';
import { LineViewVehicle } from './line-view.models';
import { LineViewMinimapComponent } from './line-view-minimap/line-view-minimap.component';
import {
    VehicleTooltipComponent,
    VehicleTooltipData,
} from '@modules/shared/components/vehicle-tooltip/vehicle-tooltip';

// Re-export so existing imports from this file continue to work.
export type {
    LineViewStop,
    LineViewVariant,
    LineViewVehicle,
} from './line-view.models';

@Component({
    selector: 'app-line-view',
    templateUrl: './line-view.component.html',
    styleUrls: ['./line-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, OverlayModule, LineViewMinimapComponent],
})
export class LineViewComponent implements AfterViewInit, OnDestroy {
    private readonly _lineViewService = inject(LineViewService);
    private readonly _router = inject(Router);
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _overlay = inject(Overlay);
    private readonly _viewContainerRef = inject(ViewContainerRef);

    @ViewChild('scrollArea', { read: ElementRef })
    private _scrollArea!: ElementRef<HTMLElement>;

    private readonly _containerWidth = signal<number>(0);
    private _resizeObserver: ResizeObserver | null = null;

    /**
     * Maximum allowed connector width so vehicles never overflow the panel.
     * Each side of the grid is (containerWidth - trackWidth) / 2.
     * We subtract the pin width (39px) and a small margin (8px) to keep it flush.
     */
    private readonly _maxConnectorWidth = computed(() => {
        const w = this._containerWidth();
        if (w === 0) return Number.MAX_SAFE_INTEGER;
        const TRACK_WIDTH = 40;
        const PIN_WIDTH = 39;
        const MARGIN = 8;
        return Math.max(40, (w - TRACK_WIDTH) / 2 - PIN_WIDTH - MARGIN);
    });

    readonly stops = computed(() => this._lineViewService.activeStops());
    readonly variants = computed(() => this._lineViewService.activeVariants());
    readonly vehicles = computed(() => this._lineViewService.vehicles());
    readonly variantIndicesWithVehicles = computed(() => {
        const set = new Set<number>();
        for (const v of this.vehicles()) set.add(v.variantIndex);
        return set;
    });
    readonly hasVehiclesWithFailedActivities = computed(() =>
        this._lineViewService.hasVehiclesWithFailedActivities(),
    );

    readonly variableSpacingEnabled =
        this._lineViewService.variableSpacingEnabled;
    readonly continuousMotionEnabled =
        this._lineViewService.continuousMotionEnabled;

    readonly stopChipMinWidth = computed(() => {
        const total = this.variants().length;
        if (total <= 1) return 0;
        const totalVariantsWidth = total * 3 + (total - 1) * 5;
        // content-box: subtract chip padding (12px) + border (2px), add 8px for 4px buffer each side
        return Math.max(0, totalVariantsWidth - 2);
    });

    hoveredStopIndex: number | null = null;
    hoveredVariantIndex: number | null = null;
    hoveredVehicleInventoryNr: string | null = null;
    selectedVariantIndex: number | null = null;

    private _hideTooltipTimer: ReturnType<typeof setTimeout> | null = null;
    private _overlayRef: OverlayRef | null = null;
    private _tooltipRef: ComponentRef<VehicleTooltipComponent> | null = null;

    ngAfterViewInit(): void {
        this._resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? 0;
            this._containerWidth.set(width);
            this._cdr.markForCheck();
        });
        this._resizeObserver.observe(this._scrollArea.nativeElement);
    }

    ngOnDestroy(): void {
        this._resizeObserver?.disconnect();
        if (this._hideTooltipTimer !== null) {
            clearTimeout(this._hideTooltipTimer);
        }
        this._closeTooltip();
    }

    getVehiclesAtPosition(
        stopIndex: number,
        side: 'left' | 'right',
    ): LineViewVehicle[] {
        return this.vehicles().filter((v) => {
            const isAtThisStop =
                v.atStop && Math.round(v.stopPosition) === stopIndex;
            return side === 'right'
                ? isAtThisStop && v.goingDown
                : isAtThisStop && !v.goingDown;
        });
    }

    getVehiclesBetween(
        stopIndex: number,
        side: 'left' | 'right',
    ): LineViewVehicle[] {
        return this.vehicles().filter((v) => {
            const isBetween =
                !v.atStop &&
                Math.floor(v.stopPosition) === stopIndex &&
                v.stopPosition !== stopIndex;
            return side === 'right'
                ? isBetween && v.goingDown
                : isBetween && !v.goingDown;
        });
    }

    /**
     * Vertical offset (CSS translateY) for a vehicle marker inside a segment row.
     * Maps the vehicle's fractional progress along the segment to a pixel offset
     * relative to the row centre so the marker tracks its real position:
     *   frac ≈ 0  → near top of row (just left the upper stop)
     *   frac = 0.5 → centre of row
     *   frac ≈ 1  → near bottom of row (about to arrive at lower stop)
     * The row heights mirror the SCSS variables ($segment-height-*).
     *
     * The offset is capped so the connector dot stays at least STOP_CHIP_BUFFER px
     * inside the segment boundary, preventing the vehicle from visually merging with
     * the adjacent stop chip (which overflows ~1px into the segment row).
     */
    getSegmentTranslateY(vehicle: LineViewVehicle, stopIndex: number): string {
        const frac = vehicle.stopPosition - Math.floor(vehicle.stopPosition);
        const spacingClass = this.variableSpacingEnabled
            ? this.stops()[stopIndex]?.spacingClass
            : undefined;
        const rowHeight =
            spacingClass === 'extra-large'
                ? 96
                : spacingClass === 'large'
                  ? 48
                  : spacingClass === 'medium'
                    ? 24
                    : spacingClass === 'short'
                      ? 12
                      : 18; // $row-height default

        // Stop chip is ~20px tall, overflowing the 18px stop row by ~1px per side.
        // Keep the connector dot at least 4px inside the segment boundary so the
        // vehicle is clearly visually separate from the stop chip.
        const STOP_CHIP_BUFFER = 4; // px
        const maxOffset = Math.max(1, rowHeight / 2 - STOP_CHIP_BUFFER);
        const offset = Math.min(
            Math.max((frac - 0.5) * rowHeight, -maxOffset),
            maxOffset,
        );
        return `translateY(${offset}px)`;
    }

    /** Min-width for the connector line.
     *  Base = half the longest stop chip width (so the vehicle clears the chip),
     *  plus 38px per additional vehicle so they don't overlap each other.
     *  Also accounts for vehicles in the adjacent row above on the same side,
     *  pushing this row's vehicles further out to prevent vertical overlap. */
    getConnectorMinWidth(
        vehicleIndex: number,
        stopIndex: number,
        side: 'left' | 'right',
        isSegment: boolean,
    ): number {
        let neighborCount = 0;
        if (isSegment) {
            // Segment row between stop i and i+1: check stop-row i above
            neighborCount = this.getVehiclesAtPosition(stopIndex, side).length;
        } else if (stopIndex > 0) {
            // Stop row i: check segment-row above (between stop i-1 and i)
            neighborCount = this.getVehiclesBetween(stopIndex - 1, side).length;
        }
        return Math.min(
            this._baseConnectorWidth + (vehicleIndex + neighborCount) * 38,
            this._maxConnectorWidth(),
        );
    }

    private get _baseConnectorWidth(): number {
        const stops = this.stops();
        if (stops.length === 0) return 40;
        const longestName = stops.reduce(
            (longest, s) => (s.name.length > longest.length ? s.name : longest),
            '',
        );
        // ~6.5px per char at 12px Inter + 12px padding + 2px border = chip width
        // Vehicle needs to clear half of that (chip is centered on track)
        const chipWidth = longestName.length * 6.5 + 14;
        return Math.max(40, chipWidth / 2 + 8);
    }

    // ─── Hover highlight / dim ───────────────────────────────────────

    isVariantHighlighted(variantIndex: number): boolean {
        if (this.hoveredVariantIndex !== null)
            return this.hoveredVariantIndex === variantIndex;
        if (this.selectedVariantIndex !== null)
            return this.selectedVariantIndex === variantIndex;
        if (this.hoveredStopIndex !== null) {
            const v = this.variants()[variantIndex];
            if (!v) return false;
            const stopName =
                this.stops()[this.hoveredStopIndex]?.name?.toLowerCase();
            if (!stopName) return false;
            return v.stopNames.some((s) => s.toLowerCase() === stopName);
        }
        return false;
    }

    isVariantDimmed(variantIndex: number): boolean {
        if (!this.variantIndicesWithVehicles().has(variantIndex)) return true;
        if (
            this.hoveredVariantIndex === null &&
            this.selectedVariantIndex === null &&
            this.hoveredStopIndex === null
        )
            return false;
        return !this.isVariantHighlighted(variantIndex);
    }

    isStopDimmed(stopIndex: number): boolean {
        const variantIdx =
            this.hoveredVariantIndex ?? this.selectedVariantIndex;
        if (variantIdx === null) return false;
        const v = this.variants()[variantIdx];
        if (!v) return true;
        const stopName = this.stops()[stopIndex]?.name?.toLowerCase();
        if (!stopName) return true;
        return !v.stopNames.some((s) => s.toLowerCase() === stopName);
    }

    isVehicleDimmed(vehicle: LineViewVehicle): boolean {
        if (this.hoveredVariantIndex !== null)
            return vehicle.variantIndex !== this.hoveredVariantIndex;
        if (this.selectedVariantIndex !== null)
            return vehicle.variantIndex !== this.selectedVariantIndex;
        if (this.hoveredStopIndex !== null)
            return !this.isVariantHighlighted(vehicle.variantIndex);
        return false;
    }

    onVariantClick(variantIndex: number): void {
        this.selectedVariantIndex = variantIndex;
    }

    onContainerClick(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (
            target?.closest(
                '.track-line-variant-wrapper, .vehicle-marker, .stop-chip',
            )
        ) {
            return;
        }
        this.selectedVariantIndex = null;
    }

    /** Left offset for the 11px-wide variant hit area (centers on same position as 3px line) */
    getVariantHitOffset(variantIndex: number): number {
        return this.getVariantOffset(variantIndex) - 4;
    }

    getVariantOffset(variantIndex: number): number {
        const total = this.variants().length;
        const lineWidth = 3;
        const gap = 5;
        const totalWidth = total * lineWidth + (total - 1) * gap;
        const trackWidth = 40;
        return (trackWidth - totalWidth) / 2 + variantIndex * (lineWidth + gap);
    }

    /** Get the center X position of a variant line within the track cell */
    getVariantCenterX(variantIndex: number): number {
        return this.getVariantOffset(variantIndex) + 1.5; // half of 3px line width
    }

    /** Negative margin to push connector-dot into track cell onto its variant line.
     *  For left vehicles: margin-right pulls dot right into track cell.
     *  For right vehicles: margin-left pulls dot left into track cell. */
    getDotMargin(variantIndex: number, side: 'left' | 'right'): number {
        const centerX = this.getVariantCenterX(variantIndex);
        const trackWidth = 40;
        const dotRadius = 3.5;
        if (side === 'left') {
            // left vehicle: dot is rightmost element, needs to go right into track
            return -(centerX + dotRadius);
        }
        // right vehicle: dot is leftmost element, needs to go left into track
        return -(trackWidth - centerX + dotRadius);
    }

    onVehicleClick(vehicle: LineViewVehicle): void {
        this._router.navigate([
            '',
            {
                outlets: {
                    'outlet-right1': ['vehicle', vehicle.inventoryNumber],
                    'outlet-right2': null,
                },
            },
        ]);
    }

    onVehicleMouseEnter(event: MouseEvent, vehicle: LineViewVehicle): void {
        if (this._hideTooltipTimer !== null) {
            clearTimeout(this._hideTooltipTimer);
            this._hideTooltipTimer = null;
        }
        this.hoveredVehicleInventoryNr = vehicle.inventoryNumber;
        this.hoveredVariantIndex = vehicle.variantIndex;
        this._openTooltip(event.currentTarget as HTMLElement, vehicle);
        this._cdr.markForCheck();
    }

    onVehicleMouseLeave(): void {
        this._scheduleTooltipHide();
    }

    onTooltipMouseEnter(): void {
        if (this._hideTooltipTimer !== null) {
            clearTimeout(this._hideTooltipTimer);
            this._hideTooltipTimer = null;
        }
    }

    onTooltipMouseLeave(): void {
        this._scheduleTooltipHide();
    }

    private _scheduleTooltipHide(): void {
        this._hideTooltipTimer = setTimeout(() => {
            this._closeTooltip();
            this.hoveredVehicleInventoryNr = null;
            this.hoveredVariantIndex = null;
            this._hideTooltipTimer = null;
            this._cdr.markForCheck();
        }, 80);
    }

    private _openTooltip(origin: HTMLElement, vehicle: LineViewVehicle): void {
        this._closeTooltip();

        const positionStrategy = this._overlay
            .position()
            .flexibleConnectedTo(origin)
            .withPositions([
                {
                    originX: 'center',
                    originY: 'top',
                    overlayX: 'center',
                    overlayY: 'bottom',
                    offsetY: -4,
                },
                {
                    originX: 'center',
                    originY: 'bottom',
                    overlayX: 'center',
                    overlayY: 'top',
                    offsetY: 4,
                },
            ])
            .withPush(true)
            .withFlexibleDimensions(false);

        this._overlayRef = this._overlay.create({
            positionStrategy,
            scrollStrategy: this._overlay.scrollStrategies.reposition(),
            panelClass: 'line-view-tooltip-panel',
            hasBackdrop: false,
        });

        const portal = new ComponentPortal(
            VehicleTooltipComponent,
            this._viewContainerRef,
        );
        this._tooltipRef = this._overlayRef.attach(portal);
        this._tooltipRef.setInput('data', this._toTooltipData(vehicle));
        this._tooltipRef.instance.tooltipEnter.subscribe(() =>
            this.onTooltipMouseEnter(),
        );
        this._tooltipRef.instance.tooltipLeave.subscribe(() =>
            this.onTooltipMouseLeave(),
        );
    }

    private _closeTooltip(): void {
        if (this._overlayRef) {
            this._overlayRef.detach();
            this._overlayRef.dispose();
            this._overlayRef = null;
            this._tooltipRef = null;
        }
    }

    private _toTooltipData(v: LineViewVehicle): VehicleTooltipData {
        return {
            inventoryNumber: v.inventoryNumber,
            deviationText: v.deviationText,
            deviationMinutes: v.deviationMinutes,
            deviationColor: v.color,
            currentActivity: v.currentActivity,
            nextActivity: v.nextActivity,
            activitiesFailed: v.activitiesFailed,
            activitiesLoading: !v.currentActivity && !v.activitiesFailed,
            warnings: v.warnings,
        };
    }
}
